import { useCallback, useEffect, useMemo, useState } from 'react';
import WorkflowTimelineBottomBar from '../../components/common/WorkflowTimelineBottomBar';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import StreamRoundedIcon from '@mui/icons-material/StreamRounded';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
const TIMELINE_POLL_INTERVAL_MS = 1500;

const getCurrentUserName = () => {
  const rawUser = sessionStorage.getItem('user') || localStorage.getItem('user');
  if (!rawUser) return '';

  try {
    const parsedUser = JSON.parse(rawUser);
    return parsedUser?.username || parsedUser?.name || '';
  } catch {
    return '';
  }
};

const getRequestBundle = (data) => {
  if (Array.isArray(data)) return data[0] || null;
  if (Array.isArray(data?.data)) return data.data[0] || null;
  return data || null;
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

const pickFirst = (...values) =>
  values.find((value) => {
    if (Array.isArray(value)) return value.filter(Boolean).length > 0;
    return value !== undefined && value !== null && value !== '';
  });

const fieldValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
};

const joinParts = (...values) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value) => value !== undefined && value !== null && value !== '' && value !== '-')
    .join(', ');

const formatTimestamp = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(
    now.getHours()
  )}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

const formatActiveStatus = (value) => {
  if (!value) return '-';
  if (value === 'A') return 'Active';
  if (value === 'I') return 'Inactive';
  return String(value);
};

const buildSearchQuery = (request) => {
  const updatedHcp = request?.updatedHcp || {};
  const isnIds = normalizeToArray(updatedHcp.isnId);
  const mdmId = updatedHcp.mdmId;

  if (isnIds.length === 0) return null;

  const isnQuery = isnIds.map((id) => `isn_id:${id}`).join(' OR ');
  const mdmFilter = mdmId ? ` AND -mdm_id:${mdmId}` : '';
  return `q=(${isnQuery})${mdmFilter} AND -is_deleted:true&rows=100`;
};

const SelectionRadioCell = ({ row, selectedRowId, onSelect }) => {
  const rowId = String(row.original.mdm_id || row.original.mdmId || '');

  return (
    <Radio
      checked={rowId === String(selectedRowId)}
      onChange={() => onSelect(rowId)}
      size="small"
    />
  );
};

SelectionRadioCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      mdm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      mdmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
  }).isRequired,
  selectedRowId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const getTimelineVisuals = (step = {}, index = 0) => {
  const title = String(
    step?.title || step?.name || step?.eventName || step?.stepName || `step ${index + 1}`
  ).toLowerCase();
  const status = String(step?.status || '').toLowerCase();
  const type = String(step?.type || '').toLowerCase();
  const key = `${title} ${status} ${type}`;

  if (key.includes('agent started') || key.includes('trigger')) {
    return { accent: '#22c55e', icon: BoltIcon };
  }
  if (
    key.includes('metadata') ||
    key.includes('read') ||
    key.includes('retrieving prescription') ||
    key.includes('validating provider identity')
  ) {
    return { accent: '#a78bfa', icon: PsychologyAltRoundedIcon };
  }
  if (
    key.includes('retrieved hcp profile') ||
    key.includes('lookup') ||
    key.includes('internet') ||
    key.includes('master data') ||
    key.includes('public sources') ||
    key.includes('address performance')
  ) {
    return { accent: '#38bdf8', icon: PublicRoundedIcon };
  }
  if (
    key.includes('confidence') ||
    key.includes('decision') ||
    key.includes('evaluate') ||
    key.includes('evidence verified') ||
    key.includes('generating recommendation')
  ) {
    return { accent: '#fb923c', icon: ShieldRoundedIcon };
  }
  if (key.includes('recommendation ready')) {
    return { accent: '#f59e0b', icon: MonitorHeartRoundedIcon };
  }
  if (key.includes('human review')) {
    return { accent: '#f97316', icon: HourglassTopRoundedIcon };
  }
  if (key.includes('automatic approval completed')) {
    return { accent: '#16a34a', icon: CheckCircleRoundedIcon };
  }

  const fallbackVisuals = [
    { accent: '#38bdf8', icon: StreamRoundedIcon },
    { accent: '#22c55e', icon: BoltIcon },
    { accent: '#a78bfa', icon: PsychologyAltRoundedIcon },
    { accent: '#38bdf8', icon: PublicRoundedIcon },
    { accent: '#fb923c', icon: ShieldRoundedIcon },
  ];

  return fallbackVisuals[index] || fallbackVisuals[fallbackVisuals.length - 1];
};

const buildMockWorkflowSteps = ({ matchingRowsLength, isAddressIssue }) => {
  const relatedCandidates = matchingRowsLength || 3;

  if (isAddressIssue) {
    return [
      {
        title: 'Agent Started Review',
        subtitle: 'The workflow agent picked up this address change request and opened an automated review lane.',
        accent: '#22c55e',
        icon: BoltIcon,
        state: 'completed',
      },
      {
        title: 'Retrieved HCP Profile',
        subtitle: 'Golden record profile, source lineage, and provider context were loaded successfully.',
        accent: '#38bdf8',
        icon: StreamRoundedIcon,
        state: 'completed',
      },
      {
        title: 'Retrieving Prescription Data',
        subtitle: 'Prescription and downstream usage signals are being gathered to validate the requested location update.',
        accent: '#a78bfa',
        icon: PsychologyAltRoundedIcon,
        state: 'running',
      },
      {
        title: 'Comparing Address Performance',
        subtitle: `The agent is checking ${relatedCandidates} related evidence sources for consistency, deliverability, and usage confidence.`,
        accent: '#38bdf8',
        icon: PublicRoundedIcon,
        state: 'idle',
      },
      {
        title: 'Generating Recommendation',
        subtitle: 'Conflicting location signals were detected, so the agent prepared a steward-facing recommendation package.',
        accent: '#fb923c',
        icon: ShieldRoundedIcon,
        state: 'idle',
      },
      {
        title: 'Recommendation Ready',
        subtitle: 'The suggested correction, supporting evidence, and steward notes have been assembled.',
        accent: '#f59e0b',
        icon: MonitorHeartRoundedIcon,
        state: 'idle',
      },
      {
        title: 'Waiting For Human Review',
        subtitle: 'This mock state is intentionally left open to show the work paused in the human approval queue.',
        accent: '#f97316',
        icon: HourglassTopRoundedIcon,
        state: 'idle',
      },
    ];
  }

  return [
    {
      title: 'Agent Started Review',
      subtitle: 'The name-change DCR was accepted by the autonomous workflow agent and moved into active evaluation.',
      accent: '#22c55e',
      icon: BoltIcon,
      state: 'completed',
    },
    {
      title: 'Retrieved HCP Profile',
      subtitle: 'Current mastered attributes, prior stewardship actions, and source systems were loaded for comparison.',
      accent: '#38bdf8',
      icon: StreamRoundedIcon,
      state: 'completed',
    },
    {
      title: 'Searching Public Sources',
      subtitle: 'External provider registries and reference sources were queried to confirm the revised physician identity.',
      accent: '#38bdf8',
      icon: PublicRoundedIcon,
      state: 'completed',
    },
    {
      title: 'Validating Provider Identity',
      subtitle: 'Cross-source identity checks completed successfully against mastered records and external evidence.',
      accent: '#a78bfa',
      icon: PsychologyAltRoundedIcon,
      state: 'completed',
    },
    {
      title: 'Evidence Verified',
      subtitle: 'All required matching signals aligned, so the change was eligible for autonomous approval.',
      accent: '#34d399',
      icon: ShieldRoundedIcon,
      state: 'completed',
    },
    {
      title: 'Automatic Approval Completed',
      subtitle: 'The update was auto-approved and the audit trail was generated without human intervention.',
      accent: '#16a34a',
      icon: CheckCircleRoundedIcon,
      state: 'completed',
    },
  ];
};



const extractTimelineItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.steps)) return response.steps;
  if (Array.isArray(response?.timeline)) return response.timeline;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  if (Array.isArray(response?.data?.steps)) return response.data.steps;
  if (Array.isArray(response?.data?.timeline)) return response.data.timeline;
  return [];
};

const normalizeTimelineSteps = (items = []) =>
  items
    .filter(Boolean)
    .map((item, index) => {
      const visuals = getTimelineVisuals(item, index);
      const title =
        item?.title || item?.name || item?.eventName || item?.stepName || `Step ${index + 1}`;
      const subtitle =
        item?.subtitle ||
        item?.description ||
        item?.detail ||
        item?.message ||
        item?.summary ||
        'Timeline event received from workflow service.';

      return {
        title: /^\d+\./.test(String(title)) ? String(title) : `${index + 1}. ${title}`,
        subtitle: String(subtitle),
        accent: visuals.accent,
        icon: visuals.icon,
        active: item?.active !== false,
        state: item?.state || item?.status || (item?.active === false ? 'idle' : 'completed'),
      };
    });



const HCPMerge = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { loading, error, execute } = useAPICall();
  const { execute: executeTimeline } = useAPICall();
  const batchId = searchParams.get('batchId');
  const reqId = searchParams.get('reqId');
  const userName = useMemo(() => getCurrentUserName(), []);

  const [requestBundle, setRequestBundle] = useState(null);
  const [requestLoadError, setRequestLoadError] = useState('');
  const [matchingRows, setMatchingRows] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState('');
  const [mergeReason, setMergeReason] = useState('');
  const [unmergeReason, setUnmergeReason] = useState('');
  const [unmergeSource, setUnmergeSource] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [toBeMastered, setToBeMastered] = useState(false);
  const [submittingAction, setSubmittingAction] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [unmergeOpen, setUnmergeOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [timelineLastUpdated, setTimelineLastUpdated] = useState(null);
  const [timelineUsesMockData, setTimelineUsesMockData] = useState(false);
  
  ;

  const requestObj = useMemo(() => requestBundle?.requests?.[0] || null, [requestBundle]);
  const updatedHcp = useMemo(
    () => requestObj?.updatedHcp || requestObj?.accountDetails || {},
    [requestObj]
  );

  const loadMatchingRecords = useCallback(
    async (request) => {
      const query = buildSearchQuery(request);
      if (!query) {
        setMatchingRows([]);
        return;
      }

      setLoadingMatches(true);
      try {
        const response = await execute(
          API_CONFIG.hcp.search,
          { q: query },
          {
            showErrorToast: false,
            errorMessage: 'Unable to load matching HCPs.',
          }
        );
        const docs = response?.response?.docs || response?.data?.response?.docs || [];
        setMatchingRows(Array.isArray(docs) ? docs : []);
      } catch {
        setMatchingRows([]);
      } finally {
        setLoadingMatches(false);
      }
    },
    [execute]
  );

  useEffect(() => {
    const loadRequest = async () => {
      if (!batchId || !reqId) return;

      try {
        setRequestLoadError('');
        const response = await execute(API_CONFIG.workflow.getHcpBatchRequest, null, {
          params: { batchId, reqId },
          showErrorToast: false,
          errorMessage: 'Unable to load DCR details.',
        });
        const bundle = getRequestBundle(response);
        setRequestBundle(bundle);
        await loadMatchingRecords(bundle?.requests?.[0]);
      } catch {
        setRequestLoadError('Unable to load DCR details.');
      }
    };

    loadRequest();
  }, [batchId, execute, loadMatchingRecords, reqId]);

  const requestName = useMemo(() => {
    const firstName = pickFirst(updatedHcp.fName, updatedHcp.firstName);
    const middleName = pickFirst(updatedHcp.mName, updatedHcp.middleName);
    const lastName = pickFirst(updatedHcp.lName, updatedHcp.lastName);
    return [firstName, middleName, lastName].filter(Boolean).join(' ') || 'Incoming physician update';
  }, [updatedHcp]);

  const requestTypeLabel = useMemo(
    () => fieldValue(requestObj?.requestIssueType || requestObj?.requestType || 'DCR'),
    [requestObj]
  );

  const requestStatus = useMemo(
    () => fieldValue(requestObj?.requestStatus || 'In Progress'),
    [requestObj]
  );

  const requestCreatedAt = useMemo(
    () =>
      fieldValue(
        requestObj?.createdAt ||
        requestObj?.createdDate ||
        requestObj?.createdOn ||
        requestObj?.crtTs ||
        requestBundle?.createdAt
      ),
    [requestBundle?.createdAt, requestObj]
  );

  const selectedRow = useMemo(
    () =>
      matchingRows.find((row) => String(row.mdm_id || row.mdmId) === String(selectedRowId)) || null,
    [matchingRows, selectedRowId]
  );

  const selectedSources = useMemo(
    () =>
      String(selectedRow?.source || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    [selectedRow]
  );

  const isAddressIssue = useMemo(
    () => String(requestObj?.requestIssueType || '').toUpperCase().includes('ADDRESS'),
    [requestObj]
  );

  const autoConfidence = isAddressIssue ? 74 : 98;
  const autoResolved = autoConfidence >= 90;

  const effectiveAddress = useMemo(() => {
    if (isAddressIssue && !['MERGED', 'CLOSED'].includes(String(requestObj?.requestStatus || ''))) {
      return requestObj?.addr || updatedHcp.addr || {};
    }
    return updatedHcp.primaryAddress || updatedHcp.addr || requestObj?.addr || {};
  }, [isAddressIssue, requestObj?.addr, requestObj?.requestStatus, updatedHcp.addr, updatedHcp.primaryAddress]);

  const requestAddress = useMemo(
    () => ({
      source: pickFirst(effectiveAddress.source, updatedHcp.source),
      line1: pickFirst(effectiveAddress.addrLn1, effectiveAddress.address1, updatedHcp.addressLine),
      line2: pickFirst(
        effectiveAddress.addrLn2,
        effectiveAddress.address2,
        updatedHcp.addressLine2
      ),
      city: pickFirst(effectiveAddress.cityNm, effectiveAddress.city, updatedHcp.city),
      state: pickFirst(effectiveAddress.stCd, effectiveAddress.state, updatedHcp.state),
      zip5: pickFirst(
        effectiveAddress.zipCd?.[0],
        effectiveAddress.zip5,
        effectiveAddress.zip,
        updatedHcp.zip
      ),
      zip4: pickFirst(effectiveAddress.zipCd?.[1], effectiveAddress.zip4, updatedHcp.zip4),
    }),
    [
      effectiveAddress,
      updatedHcp.addressLine,
      updatedHcp.addressLine2,
      updatedHcp.city,
      updatedHcp.state,
      updatedHcp.source,
      updatedHcp.zip,
      updatedHcp.zip4,
    ]
  );

  const sameSource = useMemo(() => {
    const requestSources = String(updatedHcp.source || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return requestSources.some((source) => selectedSources.includes(source));
  }, [selectedSources, updatedHcp.source]);

  const canUnmerge = useMemo(
    () => selectedSources.length > 1 && !sameSource,
    [sameSource, selectedSources.length]
  );

  const canMerge = useMemo(() => {
    if (!selectedRow || sameSource) return false;
    const status = String(selectedRow.status || '').toLowerCase();
    if (status === 'inactive') return false;

    const requestIds = [updatedHcp.npiId, updatedHcp.imsId].filter(Boolean).map(String);
    const selectedIsnIds = normalizeToArray(selectedRow.isn_id || selectedRow.isnId).map(String);
    return requestIds.some((id) => selectedIsnIds.includes(id));
  }, [sameSource, selectedRow, updatedHcp.imsId, updatedHcp.npiId]);

  const formatMatchField = useCallback((row, ...keys) => {
    for (const key of keys) {
      if (Array.isArray(row?.[key])) {
        const joined = row[key].filter(Boolean).join(', ');
        if (joined) return joined;
      }
      if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '') {
        return row[key];
      }
    }
    return '-';
  }, []);

  const decisionCards = useMemo(
    () => [
      {
        title: 'Name Change DCR',
        status: 'AI Auto Resolved',
        confidence: 98,
        badgeLabel: 'Resolved by AI Agent',
        badgeColor: '#34d399',
        accent: '#34d399',
        details: [
          'Confidence: High',
          'Public Registry Match',
          'Audit Log Generated',
          'Auto-approved',
        ],
        reasoning:
          'The agent matched the physician name revision against a public provider registry, existing source lineage, and prior mastered identities. All signals agreed, so the update was automatically applied and the DCR was closed.',
      },
      {
        title: 'Address Change DCR',
        status: 'Recommendation Requires Steward Approval',
        confidence: 74,
        badgeLabel: 'Human Review Required',
        badgeColor: '#fb923c',
        accent: '#fb923c',
        details: [
          'Recommendation Generated',
          'Suggested Corrected Address',
          'Confidence: Medium',
          'Escalation Sent',
        ],
        reasoning:
          'The agent identified conflicting suite and postal details across external validation sources. It proposed a normalized address candidate but held the update for a steward because certainty did not cross the autonomous threshold.',
      },
    ],
    []
  );

  const fallbackWorkflowSteps = useMemo(
    () =>
      buildMockWorkflowSteps({
        matchingRowsLength: matchingRows.length,
        isAddressIssue,
      }),
    [isAddressIssue, matchingRows.length]
  );

  const agentLogs = useMemo(
    () => [
      `08:42:11 Agent subscribed to DCR stream for batch ${fieldValue(batchId)}.`,
      `08:42:14 Parsed request ${fieldValue(reqId)} for ${requestName}.`,
      `08:42:18 Compared against ${matchingRows.length || 3} related HCP candidates in master data.`,
      `08:42:23 External validation completed for ${requestTypeLabel}.`,
      autoResolved
        ? '08:42:27 Resolution threshold exceeded. Golden record updated automatically.'
        : '08:42:27 Confidence below threshold. Steward recommendation packet created.',
      autoResolved
        ? '08:42:30 Audit trail persisted and close event emitted.'
        : '08:42:30 Escalation notification delivered to human approval queue.',
    ],
    [autoResolved, batchId, matchingRows.length, reqId, requestName, requestTypeLabel]
  );

  const topSummaryFields = useMemo(
    () => [
      { label: 'MDM ID', value: fieldValue(updatedHcp.mdmId || requestObj?.requestMdmId) },
      { label: 'Source', value: fieldValue(updatedHcp.source) },
      { label: 'First Name', value: fieldValue(pickFirst(updatedHcp.fName, updatedHcp.firstName)) },
      { label: 'Middle Name', value: fieldValue(pickFirst(updatedHcp.mName, updatedHcp.middleName)) },
      { label: 'Last Name', value: fieldValue(pickFirst(updatedHcp.lName, updatedHcp.lastName)) },
      { label: 'Suffix', value: fieldValue(pickFirst(updatedHcp.suffix, updatedHcp.sfxCode)) },
      { label: 'Gender', value: fieldValue(updatedHcp.gender) },
      { label: 'Birth Year', value: fieldValue(updatedHcp.birthYear || updatedHcp.birthYr || updatedHcp.dobYear) },
      {
        label: 'Status',
        value: fieldValue(
          formatActiveStatus(pickFirst(updatedHcp.status, updatedHcp.activeStatusCode, requestObj?.requestStatus))
        ),
      },
      { label: 'Address Source', value: fieldValue(requestAddress.source) },
      { label: 'Address Line 1', value: fieldValue(requestAddress.line1) },
      { label: 'Address Line 2', value: fieldValue(requestAddress.line2) },
      { label: 'City', value: fieldValue(requestAddress.city) },
      { label: 'State', value: fieldValue(requestAddress.state) },
      { label: 'Zip5', value: fieldValue(requestAddress.zip5) },
      { label: 'Zip4', value: fieldValue(requestAddress.zip4) },
      { label: 'NPI ID', value: fieldValue(updatedHcp.npiId) },
      { label: 'IMS ID', value: fieldValue(updatedHcp.imsId) },
      {
        label: 'Primary Profession',
        value: fieldValue(pickFirst(updatedHcp.primPrf, updatedHcp.primaryProfession)),
      },
      {
        label: 'Primary Specialty',
        value: fieldValue(
          pickFirst(
            updatedHcp.primSpcl,
            updatedHcp.primarySpecialty,
            updatedHcp.primarySpeciality
          )
        ),
      },
    ],
    [requestAddress, requestObj, updatedHcp]
  );

  const loadTimelineEvents = useCallback(async () => {
    if (!batchId || !reqId) {
      setWorkflowSteps([]);
      setTimelineUsesMockData(false);
      return;
    }

    try {
      const response = await executeTimeline(API_CONFIG.workflow.getHcpTimelineEvents, null, {
        params: { batchId, reqId },
        showErrorToast: false,
        errorMessage: 'Unable to load workflow timeline.',
      });

      const items = extractTimelineItems(response);
      const normalized = normalizeTimelineSteps(items);

      if (normalized.length > 0) {
        setWorkflowSteps(normalized);
        setTimelineLastUpdated(new Date().toISOString());
        setTimelineUsesMockData(false);
        return;
      }

      setWorkflowSteps([]);
      setTimelineUsesMockData(false);
    } catch {
      setWorkflowSteps([]);
      setTimelineUsesMockData(false);
    }
  }, [batchId, executeTimeline, reqId]);

  useEffect(() => {
    const loadRequest = async () => {
      if (!batchId || !reqId) return;

      try {
        setRequestLoadError('');
        const response = await execute(API_CONFIG.workflow.getHcpBatchRequest, null, {
          params: { batchId, reqId },
          showErrorToast: false,
          errorMessage: 'Unable to load DCR details.',
        });
        const bundle = getRequestBundle(response);
        setRequestBundle(bundle);
        await loadMatchingRecords(bundle?.requests?.[0]);
      } catch {
        setRequestLoadError('Unable to load DCR details.');
      }
    };

    loadRequest();
  }, [batchId, execute, loadMatchingRecords, reqId]);

  useEffect(() => {
    setWorkflowSteps([]);
    setTimelineUsesMockData(false);
  }, []);

  useEffect(() => {
    if (!batchId || !reqId) return undefined;

    loadTimelineEvents();
    const intervalId = window.setInterval(loadTimelineEvents, TIMELINE_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [batchId, loadTimelineEvents, reqId]);

  const openMatchingHcpDetails = useCallback(
    (row) => {
      const mdmId = row?.mdm_id || row?.mdmId;
      if (!mdmId) return;
      navigate(`/details/hcp/${mdmId}`);
    },
    [navigate]
  );

  const buildRequestPayload = useCallback(
    (reason) => {
      if (!requestBundle || !requestObj) return null;

      return {
        ...requestBundle,
        batchClosedBy: userName || requestBundle.batchClosedBy,
        batchLastUpdatedBy: userName || requestBundle.batchLastUpdatedBy,
        requests: [
          {
            ...requestObj,
            reason,
            requestUpdatedBy: userName || requestObj.requestUpdatedBy,
            requestClosedBy: userName || requestObj.requestClosedBy,
          },
        ],
      };
    },
    [requestBundle, requestObj, userName]
  );

  const handleMerge = useCallback(async () => {
    const mdmId = selectedRow?.mdm_id || selectedRow?.mdmId;
    if (!mdmId || !mergeReason.trim()) {
      toast.error('Select a matching HCP and enter a merge reason.');
      return;
    }

    const payload = buildRequestPayload(mergeReason);
    if (!payload) return;

    try {
      setSubmittingAction('merge');
      await execute(
        API_CONFIG.hcpDcr.mergeHcp,
        { data: payload },
        {
          params: { mdmId },
          // headers: userName ? { user: userName } : undefined,
          successMessage: 'Merged successfully.',
          showSuccessToast: true,
          errorMessage: 'Unable to merge HCP.',
        }
      );
      setMergeOpen(false);
      navigate(`/hcp-new-request?batchId=${batchId}`);
    } catch {
    } finally {
      setSubmittingAction('');
    }
  }, [batchId, buildRequestPayload, execute, mergeReason, navigate, selectedRow, userName]);

  const handleUnmerge = useCallback(async () => {
    const mdmId = selectedRow?.mdm_id || selectedRow?.mdmId;
    if (!mdmId || !unmergeReason.trim() || !unmergeSource.trim()) {
      toast.error('Select a matching HCP, source, and unmerge reason.');
      return;
    }

    const payload = buildRequestPayload(unmergeReason);
    if (!payload) return;

    try {
      setSubmittingAction('unmerge');
      await execute(
        API_CONFIG.hcpDcr.unmergeHcp,
        { data: payload },
        {
          params: { mdmId, source: unmergeSource },
          // headers: userName ? { user: userName } : undefined,
          successMessage: 'Unmerged successfully.',
          showSuccessToast: true,
          errorMessage: 'Unable to unmerge HCP.',
        }
      );
      setUnmergeOpen(false);
      navigate(`/hcp-new-request?batchId=${batchId}`);
    } catch {
    } finally {
      setSubmittingAction('');
    }
  }, [
    batchId,
    buildRequestPayload,
    execute,
    navigate,
    selectedRow,
    unmergeReason,
    unmergeSource,
    userName,
  ]);

  const handleCloseRequest = useCallback(async () => {
    if (!closeReason.trim()) {
      toast.error('Enter a close reason.');
      return;
    }

    const payload = buildRequestPayload(closeReason);
    if (!payload) return;

    const timestamp = formatTimestamp();
    const closePayload = {
      ...payload,
      toBeMastered,
      batchLastUpdatedTime: timestamp,
      requests: payload.requests.map((request) => ({
        ...request,
        requestLastUpdatedTime: timestamp,
      })),
    };

    try {
      setSubmittingAction('close');
      await execute(
        API_CONFIG.hcpDcr.closeRequest,
        { data: closePayload },
        {
          params: { batchId, requestId: reqId },
          // headers: userName ? { user: userName } : undefined,
          successMessage: 'Request closed successfully.',
          showSuccessToast: true,
          errorMessage: 'Unable to close request.',
        }
      );
      setCloseOpen(false);
      navigate(`/hcp-new-request?batchId=${batchId}`);
    } catch {
    } finally {
      setSubmittingAction('');
    }
  }, [
    batchId,
    buildRequestPayload,
    closeReason,
    execute,
    navigate,
    reqId,
    toBeMastered,
    userName,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 5px)',
        '@keyframes agentPulse': {
          '0%': { transform: 'translateY(0px)', boxShadow: '0 0 0 0 rgba(56,189,248,0.08)' },
          '50%': { transform: 'translateY(-2px)', boxShadow: '0 0 0 8px rgba(56,189,248,0.02)' },
          '100%': { transform: 'translateY(0px)', boxShadow: '0 0 0 0 rgba(56,189,248,0.08)' },
        },
        '@keyframes streamGlow': {
          '0%': { opacity: 0.45, transform: 'scale(0.98)' },
          '50%': { opacity: 0.9, transform: 'scale(1.02)' },
          '100%': { opacity: 0.45, transform: 'scale(0.98)' },
        },
      }}
    >
      <Box sx={{ flex: 1, pb: 4, px: { xs: 1, md: 0 } }}>
          <Box sx={{ mb: 1 }}>
            <PageBreadcrumbs items={[
          { label: 'Open Batches', path: '/inbox/system' },
          { label: `Requests in Batch ID ${batchId || '-'}`, path: `/hcp-new-request?batchId=${batchId}` },
          { label: `Request ID ${reqId || '-'}` }
        ]} />
      </Box>


      {loading ? (
        <Stack spacing={3}>

          {/* TOP SUMMARY CARD */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '8px',
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              background: "#fff",
              p: 2.5,
            }}
          >

            <Stack spacing={2}>

              {/* Header */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Skeleton variant="text" width={260} height={34} />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Skeleton variant="rounded" width={150} height={26} />
                    <Skeleton variant="rounded" width={120} height={26} />
                  </Stack>
                </Box>

                <Skeleton
                  variant="rounded"
                  width={120}
                  height={40}
                  sx={{ borderRadius: "10px" }}
                />
              </Stack>

              <Divider />

              {/* Summary Grid */}
              <Grid container spacing={2}>
                {Array.from({ length: 18 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={i}>
                    <Box>
                      <Skeleton
                        variant="text"
                        width="50%"
                        height={18}
                      />

                      <Skeleton
                        variant="text"
                        width="85%"
                        height={28}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Stack>

          </Paper>



          {/* MAIN SECTION */}
          <Grid container spacing={2}>

            {/* LEFT SIDE */}
            <Grid item xs={12} xl={7} className='p-0'>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 5,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                }}
              >

                <Stack spacing={3}>

                  {/* Header */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >

                    <Box>
                      <Skeleton
                        variant="text"
                        width={220}
                        height={34}
                      />

                      <Skeleton
                        variant="text"
                        width={320}
                        height={22}
                      />
                    </Box>

                    {/* Buttons */}
                    <Stack direction="row" spacing={1}>
                      <Skeleton
                        variant="rounded"
                        width={95}
                        height={36}
                      />

                      <Skeleton
                        variant="rounded"
                        width={95}
                        height={36}
                      />

                      <Skeleton
                        variant="rounded"
                        width={130}
                        height={36}
                      />
                    </Stack>

                  </Stack>



                  {/* Table Skeleton */}
                  <Box
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >

                    {/* Table Header */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "60px 120px 120px 140px 180px 140px 140px",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        background: "#f8fafc",
                      }}
                    >
                      {Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="text"
                          width="80%"
                          height={20}
                        />
                      ))}
                    </Box>

                    {/* Table Rows */}
                    {Array.from({ length: 6 }).map((_, row) => (
                      <Box
                        key={row}
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "60px 120px 120px 140px 180px 140px 140px",
                          gap: 2,
                          px: 2,
                          py: 2,
                          borderBottom:
                            row !== 5
                              ? "1px solid #f1f5f9"
                              : "none",
                        }}
                      >
                        {Array.from({ length: 7 }).map((_, col) => (
                          <Skeleton
                            key={col}
                            variant="text"
                            width="90%"
                            height={22}
                          />
                        ))}
                      </Box>
                    ))}

                  </Box>

                </Stack>

              </Paper>

            </Grid>



            {/* RIGHT SIDE */}
            <Grid item xs={12} xl={5} className='pt-0 pe-3'>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 5,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                }}
              >

                <Stack spacing={3}>

                  {/* Header */}
                  <Box>
                    <Skeleton
                      variant="text"
                      width={280}
                      height={34}
                    />

                    <Skeleton
                      variant="text"
                      width="90%"
                      height={22}
                    />
                  </Box>



                  {/* Workflow Steps */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      border: "1px solid #dbeafe",
                    }}
                  >

                    <Stack spacing={3}>

                      {Array.from({ length: 5 }).map((_, i) => (
                        <Stack
                          direction="row"
                          spacing={2}
                          key={i}
                        >

                          {/* Icon */}
                          <Skeleton
                            variant="circular"
                            width={42}
                            height={42}
                          />

                          {/* Content */}
                          <Box sx={{ flex: 1 }}>
                            <Skeleton
                              variant="text"
                              width="50%"
                              height={24}
                            />

                            <Skeleton
                              variant="text"
                              width="95%"
                              height={20}
                            />

                            <Skeleton
                              variant="text"
                              width="80%"
                              height={20}
                            />
                          </Box>

                        </Stack>
                      ))}

                    </Stack>

                  </Box>

                </Stack>

              </Paper>

            </Grid>

          </Grid>

        </Stack>
      ) : (
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              p: { xs: 2, md: 2.5 },
              borderRadius: '8px',
              color: '#0f172a',
              border: '1px solid',
              borderColor: alpha('#cbd5e1', 0.95),
              background: '#ffffff',
              boxShadow: '0 12px 28px rgba(15,23,42,0.06)',
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box>
                  <Typography
                    // variant="h6"
                    sx={{ color: '#0f172a', fontWeight: 600, letterSpacing: '-0.02em',fontSize:"17px" }}
                  >
                    Merge Details - Batch ID {batchId || '-'}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip
                      label={`Created: ${requestCreatedAt}`}
                      size="small"
                      sx={{
                        height: 24,
                        bgcolor: alpha('#e2e8f0', 0.5),
                        color: '#475569',
                        fontWeight: 600,
                        borderRadius: '999px',
                      }}
                    />
                    <Chip
                      label={`Status: ${String(requestStatus).toUpperCase()}`}
                      size="small"
                      sx={{
                        height: 24,
                        bgcolor: alpha('#38bdf8', 0.14),
                        color: '#0284c7',
                        fontWeight: 600,
                        borderRadius: '999px',
                      }}
                    />
                  </Stack>
                </Box>
                <Button className="action-btn process-btn action-btn process-btn"
                  variant="contained"
                  disableElevation
                  onClick={() => updatedHcp?.mdmId && navigate(`/details/hcp/${updatedHcp.mdmId}`)}
                  disabled={!updatedHcp?.mdmId}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    minWidth: 112,
                    fontWeight: 400,
                    color: '#ffffff',
                    background: 'linear-gradient(180deg, #3ba3f6 0%, #1d8ef0 100%)',
                    boxShadow: '0 8px 18px rgba(59,163,246,0.22)',
                    '&:hover': {
                      background: 'linear-gradient(180deg, #2495f3 0%, #127fdd 100%)',
                    },
                    '&.Mui-disabled': {
                      background: '#e0e0e0',
                      boxShadow: 'none',
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  View Profile
                </Button>
              </Stack>

              <Divider />

              <Grid container >
                {topSummaryFields.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.label}>
                    <Box
                      sx={{
                        minHeight: { xs: 'auto', md: 50 },
                        pr: { xs: 0, md: 1 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'rgb(105, 117, 134)',
                          fontWeight: 400,
                          // textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.15,
                          color: item.label === 'Status' ? '#22c55e' : '#000000ff',
                          fontWeight: item.label === 'Status' ? 800 : 400,
                          lineHeight: 1.6,
                          wordBreak: 'break-word',
                          
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Paper>

          <Grid container>
            <Grid item xs={12} pr={{ xl: 1 }}>
              <Stack spacing={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: alpha('#cbd5e1', 0.9),
                    background: '#ffffff',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 18px 40px rgba(15,23,42,0.08)',
                  }}
                >
                  <Stack spacing={2.5}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    >
                      <Box xs={12} sm={6} md={5} lg={2} xl={4}>
                        <Typography sx={{ color: '#0f172a', fontWeight: 600, letterSpacing: '-0.03em' }}>
                          Matching HCPs ({matchingRows.length})
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.75, color: '#475569', lineHeight: 1.7 }}>
                          Review candidate records and choose the best profile before taking action.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: { xs: 'flex-start', md: 'flex-end' },
                          pr: 0.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          flexWrap="nowrap"
                          sx={{ width: 'max-content', minWidth: 'max-content', pb: 0.5 }}
                        >
                          <Button className="action-btn close-btn action-btn close-btn"
                            variant="outlined"
                            startIcon={<SwapHorizIcon />}
                            onClick={() => setUnmergeOpen(true)}
                            disabled={!selectedRow || !canUnmerge || submittingAction !== ''}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              // px: { xs: 1.15, sm: 1.4, md: 1.6 },
                              // py: 0.8,
                              // minWidth: { xs: 96, sm: 106, md: 116 },
                              whiteSpace: 'nowrap',
                              fontWeight: 400,
                              fontSize: { xs: '0.74rem', sm: '0.78rem', md: '0.84rem' },
                              borderColor: alpha('#cbd5e1', 0.9),
                              color: '#475569',
                              '& .MuiButton-startIcon': {
                                mr: 0.6,
                                '& svg': {
                                  fontSize: { xs: 16, sm: 17, md: 18 },
                                },
                              },
                            }}
                          >
                            Unmerge
                          </Button>
                          <Button className="action-btn close-btn action-btn close-btn"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setMergeOpen(true)}
                            disabled={!selectedRow || !canMerge || submittingAction !== ''}
                             sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              // px: { xs: 1.2, sm: 1.45, md: 1.7 },
                              // py: 0.8,
                              // minWidth: { xs: 118, sm: 132, md: 146 },
                              whiteSpace: 'nowrap',
                              fontWeight: 400,
                              fontSize: { xs: '0.74rem', sm: '0.78rem', md: '0.84rem' },
                              // background: '#3b9af7',
                              '& .MuiButton-startIcon': {
                                mr: 0.6,
                                '& svg': {
                                  fontSize: { xs: 16, sm: 17, md: 18 },
                                },
                              },
                            }}
                          >
                            Merge
                          </Button>
                          <Button 
                             variant="contained" className="action-btn process-btn action-btn close-btn" 
                            disableElevation
                            startIcon={<CloseIcon />}
                            onClick={() => setCloseOpen(true)}
                            disabled={submittingAction !== ''}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              // px: { xs: 1.15, sm: 1.4, md: 1.6 },
                              // py: 0.8,
                              // minWidth: { xs: 88, sm: 96, md: 106 },
                              whiteSpace: 'nowrap',
                              fontWeight: 400,
                              fontSize: { xs: '0.74rem', sm: '0.78rem', md: '0.84rem' },
                              color: '#ffffff',
                              backgroundColor: '#10b981',
                              '& .MuiButton-startIcon': {
                                mr: 0.6,
                                '& svg': {
                                  fontSize: { xs: 16, sm: 17, md: 18 },
                                },
                              },
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: '#16a34a',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            Close Request
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>

                    {loadingMatches ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      <GlobalMaterialTable
                        columns={[
                          {
                            header: '',
                            id: 'select',
                            enableSorting: false,
                            size: 52,
                            Cell: ({ row }) => (
                              <SelectionRadioCell
                                row={row}
                                selectedRowId={selectedRowId}
                                onSelect={setSelectedRowId}
                              />
                            ),
                          },
                          {
                            header: 'Match Score',
                            id: 'matchScore',
                            size: 110,
                            accessorFn: (row) => fieldValue(formatMatchField(row, 'matchScore', 'score')),
                          },
                          {
                            header: 'Source',
                            id: 'source',
                            size: 140,
                            accessorFn: (row) => fieldValue(formatMatchField(row, 'source')),
                          },
                          {
                            header: 'MDM ID',
                            id: 'mdmId',
                            size: 130,
                            accessorFn: (row) => fieldValue(formatMatchField(row, 'mdm_id', 'mdmId')),
                          },
                          {
                            header: 'ISN ID',
                            id: 'isnId',
                            size: 240,
                            accessorFn: (row) => fieldValue(formatMatchField(row, 'isn_id', 'isnId')),
                          },
                          {
                            header: 'First Name',
                            id: 'firstName',
                            size: 130,
                            accessorFn: (row) =>
                              fieldValue(formatMatchField(row, 'fname', 'first_name', 'firstName')),
                          },
                          {
                            header: 'Middle Name',
                            id: 'middleName',
                            size: 130,
                            accessorFn: (row) =>
                              fieldValue(formatMatchField(row, 'mname', 'middle_name', 'middleName')),
                          },
                          {
                            header: 'Last Name',
                            id: 'lastName',
                            size: 130,
                            accessorFn: (row) =>
                              fieldValue(formatMatchField(row, 'lname', 'last_name', 'lastName')),
                          },
                          {
                            header: 'Status',
                            id: 'status',
                            size: 100,
                            accessorFn: (row) => fieldValue(formatMatchField(row, 'status')),
                          },
                          {
                            header: 'Address Line 1',
                            id: 'addr1',
                            size: 220,
                            accessorFn: (row) =>
                              fieldValue(
                                formatMatchField(row, 'address_line_1', 'address1', 'addressLine1')
                              ),
                          },
                          {
                            header: 'Address Line 2',
                            id: 'addr2',
                            size: 220,
                            accessorFn: (row) =>
                              fieldValue(
                                formatMatchField(row, 'address_line_2', 'address2', 'addressLine2')
                              ),
                          },
                          {
                            header: 'City',
                            id: 'city',
                            size: 130,
                            accessorFn: (row) => fieldValue(joinParts(row.city, row.cities)),
                          },
                          {
                            header: 'State',
                            id: 'state',
                            size: 100,
                            accessorFn: (row) => fieldValue(joinParts(row.state, row.states)),
                          },
                        ]}
                        data={matchingRows}
                        emptyMessage="No matching HCPs found"
                        enablePagination={false}
                        enableBottomToolbar={false}
                        getRowId={(row, index) => String(row.mdm_id || row.mdmId || index)}
                        onRowClick={(row) => setSelectedRowId(String(row.mdm_id || row.mdmId))}
                        selectedRowId={selectedRowId}
                        muiTableBodyRowProps={({ row }) => ({
                          sx: { cursor: 'pointer' },
                          onDoubleClick: () => openMatchingHcpDetails(row.original),
                        })}
                        muiTablePaperProps={{
                          elevation: 0,
                          sx: {
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: alpha('#dbeafe', 0.95),
                            overflow: 'hidden',
                            boxShadow: 'none',
                          },
                        }}
                        muiTableContainerProps={{
                          sx: {
                            maxHeight: 420,
                          },
                        }}
                      />
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Grid>

          </Grid>
        </Stack>
          )}
          </Box>        

      <Box sx={{ position: 'sticky', bottom: 0, zIndex: 1000, mt: 'auto', pb: 0, mb: -3, mx: -2 }}>
        {reqId && (
        <WorkflowTimelineBottomBar
          workflowSteps={workflowSteps}
          timelineUsesMockData={timelineUsesMockData}
          timelineLastUpdated={timelineLastUpdated}
        />
      )}
      </Box>

      <Dialog open={unmergeOpen} onClose={() => setUnmergeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Unmerge</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="MDM ID"
              value={selectedRow?.mdm_id || selectedRow?.mdmId || ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              select
              SelectProps={{ native: true }}
              label="Source"
              value={unmergeSource}
              onChange={(event) => setUnmergeSource(event.target.value)}
              fullWidth
            >
              <option value="">Select source</option>
              {selectedSources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </TextField>
            <TextField
              label="Reason"
              value={unmergeReason}
              onChange={(event) => setUnmergeReason(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button className="action-btn cancel-btn action-btn cancel-btn" onClick={() => setUnmergeOpen(false)}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={handleUnmerge} disabled={submittingAction !== ''}>
            {submittingAction === 'unmerge' ? 'Unmerging...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mergeOpen} onClose={() => setMergeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Merge</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="MDM ID - Request HCP"
              value={updatedHcp.mdmId || ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Source - Request HCP"
              value={updatedHcp.source || ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="MDM ID - Matching HCP"
              value={selectedRow?.mdm_id || selectedRow?.mdmId || ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Source - Matching HCP"
              value={selectedRow?.source || ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Reason"
              value={mergeReason}
              onChange={(event) => setMergeReason(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button className="action-btn cancel-btn action-btn cancel-btn" onClick={() => setMergeOpen(false)}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn"
            variant="contained"
            color="success"
            onClick={handleMerge}
            disabled={submittingAction !== ''}
          >
            {submittingAction === 'merge' ? 'Merging...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} fullWidth maxWidth="sm">
        <Typography className='mt-3 pb-2 px-3' sx={{ color: '#0f172a',fontSize:"16.5", fontWeight: 600, letterSpacing: '-0.02em',borderBottom:"1px solid #eaeaea" }}
                  >CLOSE REQUEST</Typography>
        <DialogContent className='py-2 pb-0 px-3'>
          <Stack spacing={0} sx={{ pt: 1 }}>
            {matchingRows.length > 0 ? (
              <Alert severity="warning">
                {matchingRows.length} matching record(s) found. Do you still want to close?
              </Alert>
            ) : null}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontWeight: 500,
                  color: '#334155',
                }}
              >
                Closed By
              </Typography>
              <TextField
                value={userName}
                InputProps={{ readOnly: true }}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 38,
                  },
                }}
              />
            </Box>
            <FormControl>
              <Typography variant="body2" sx={{ mt: 0.75, ml: 0.5 }}>
                Do you want to master the request?
              </Typography>
              <RadioGroup
                row
                value={String(toBeMastered)}
                onChange={(event) => setToBeMastered(event.target.value === 'true')}
                sx={{ ml: 0.5 }}
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>
            <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 0.5,
                mt: 0,
                fontWeight: 500,
                color: '#334155',
              }}
            >
              Reason
            </Typography>
            <TextField
              value={closeReason}
              onChange={(event) => setCloseReason(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Box>
          </Stack>
        </DialogContent>
        <DialogActions className='p-3'>
          <Button className="action-btn cancel-btn action-btn cancel-btn" onClick={() => setCloseOpen(false)}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn"
            variant="contained"
            onClick={handleCloseRequest}
            disabled={submittingAction !== ''}
          >
            {submittingAction === 'close' ? 'Closing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HCPMerge;
